import React, { useEffect, useState } from 'react';
import { Icon } from './icons.jsx';
// Drawer de criação/edição de usuário interno
const UserEditorDrawer = ({ open, user, onClose, onSave }) => {
  const isNew = !user;
  const isAuthProfile = !!user?.fromAuth;
  const profileTitle = user ? (user.displayName || user.name || user.email || 'usuário') : '';
  const emptyForm = {
    name: '', email: '', role: 'suporte', phone: '',
    sendInvite: true, active: true,
    permissions: { viewStores: true, editStores: false, suspendStores: false, impersonate: false, viewFinancial: false, editPlans: false, manageUsers: false, viewAudit: true },
  };

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [tab, setTab] = useState('info'); // info | permissions

  useEffect(() => {
    if (open) {
      if (user) {
        setForm({
          name: user.fromAuth ? (user.name || '') : (user.name || ''),
          email: user.email,
          role: user.role === 'Super admin' ? 'super_admin' : user.role === 'Admin' ? 'admin' : 'suporte',
          phone: user.phone || '',
          sendInvite: false,
          active: user.status === 'ativo',
          permissions: user.permissions || ROLE_DEFAULTS[user.role === 'Super admin' ? 'super_admin' : user.role === 'Admin' ? 'admin' : 'suporte'],
        });
      } else {
        setForm(emptyForm);
      }
      setErrors({});
      setTab('info');
    }
  }, [open, user]);

  if (!open) return null;

  const ROLES = [
    { id: 'super_admin', label: 'Super admin', desc: 'Acesso total à plataforma, incluindo usuários e configurações críticas', color: 'var(--brand-red)' },
    { id: 'admin', label: 'Admin', desc: 'Gerencia lojas, planos e financeiro. Não pode alterar usuários internos', color: 'var(--info)' },
    { id: 'suporte', label: 'Suporte', desc: 'Visualiza lojas e usa impersonação. Não acessa financeiro nem planos', color: 'var(--success)' },
  ];

  const PERMS = [
    { id: 'viewStores', label: 'Visualizar lojas', group: 'Lojas' },
    { id: 'editStores', label: 'Editar dados de lojas', group: 'Lojas' },
    { id: 'suspendStores', label: 'Suspender / ativar lojas', group: 'Lojas' },
    { id: 'impersonate', label: 'Login como lojista (impersonação)', group: 'Lojas' },
    { id: 'viewFinancial', label: 'Visualizar MRR e cobranças', group: 'Financeiro' },
    { id: 'editPlans', label: 'Criar e editar planos', group: 'Financeiro' },
    { id: 'manageUsers', label: 'Criar e editar usuários internos', group: 'Administração' },
    { id: 'viewAudit', label: 'Visualizar logs de auditoria', group: 'Administração' },
  ];

  const setRole = (roleId) => {
    setForm(f => ({ ...f, role: roleId, permissions: { ...ROLE_DEFAULTS[roleId] } }));
  };

  const togglePerm = (permId) => {
    if (form.role === 'super_admin') return; // super admin sempre tem tudo
    setForm(f => ({ ...f, permissions: { ...f.permissions, [permId]: !f.permissions[permId] } }));
  };

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(er => ({ ...er, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!isAuthProfile && !form.name.trim()) e.name = 'Nome é obrigatório';
    if (!form.email.trim()) e.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
    return e;
  };

  const handleSave = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); setTab('info'); return; }
    const roleLabel = ROLES.find(r => r.id === form.role)?.label || form.role;
    onSave({ ...user, name: form.name, email: form.email, role: roleLabel, phone: form.phone, status: form.active ? 'ativo' : 'inativo', permissions: form.permissions, sendInvite: form.sendInvite });
  };

  const inputStyle = { width: '100%', height: 36, padding: '0 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit' };
  const inputErr = { ...inputStyle, borderColor: 'var(--brand-red)' };
  const lbl = (txt, required) => (
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
      {txt}{required && <span style={{ color: 'var(--brand-red)', marginLeft: 2 }}>*</span>}
    </label>
  );
  const errMsg = (k) => errors[k] && <div style={{ fontSize: 11, color: 'var(--brand-red)', marginTop: 4 }}>{errors[k]}</div>;

  const permGroups = [...new Set(PERMS.map(p => p.group))];

  return (
    <>
      <div className="drawer-overlay" onClick={onClose} />
      <div className="drawer" style={{ width: 540 }}>
        <div className="drawer-head">
          <div style={{ width: 36, height: 36, borderRadius: 8, background: isNew ? 'var(--info-bg)' : 'var(--bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: isNew ? 'var(--info)' : 'var(--text-muted)', flexShrink: 0 }}>
            <Icon name={isNew ? 'plus' : 'edit'} size={18} />
          </div>
          <div style={{ flex: 1 }}>
            <h3>{isNew ? 'Criar usuário' : `Editar ${profileTitle}`}</h3>
            <div className="sub">{isNew ? 'Adicionar membro ao time Tezus.' : `${user.role} · ${user.email}`}</div>
          </div>
          <button className="icon-action" onClick={onClose} style={{ width: 32, height: 32 }}><Icon name="x" size={16} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '0 22px', gap: 2 }}>
          {[{ id: 'info', label: 'Informações' }, { id: 'permissions', label: 'Permissões' }].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: '10px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
              fontSize: 13, fontWeight: tab === t.id ? 600 : 400,
              color: tab === t.id ? 'var(--text)' : 'var(--text-muted)',
              borderBottom: tab === t.id ? '2px solid var(--brand-navy)' : '2px solid transparent',
              marginBottom: -1,
            }}>{t.label}</button>
          ))}
        </div>

        <div className="drawer-body">
          {tab === 'info' && (
            <>
              <div className="drawer-section">
                <h4>Dados pessoais</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    {lbl('Nome completo', !isAuthProfile)}
                    {isAuthProfile && !user.hasRealName ? (
                      <input
                        style={{ ...inputStyle, color: 'var(--text-muted)', cursor: 'not-allowed' }}
                        value=""
                        readOnly
                        placeholder="Não disponível"
                      />
                    ) : (
                      <input
                        style={errors.name ? inputErr : inputStyle}
                        value={form.name}
                        onChange={set('name')}
                        placeholder="Ex: Juliana Ferreira"
                        readOnly={isAuthProfile}
                      />
                    )}
                    {errMsg('name')}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                    <div>
                      {lbl('E-mail corporativo', true)}
                      <input style={errors.email ? inputErr : inputStyle} type="email" value={form.email} onChange={set('email')} placeholder="juliana@tezus.com" />
                      {errMsg('email')}
                    </div>
                    <div>
                      {lbl('Telefone')}
                      <input style={inputStyle} type="tel" value={form.phone} onChange={set('phone')} placeholder="(11) 99999-9999" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="drawer-section">
                <h4>Perfil de acesso</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {ROLES.map(r => (
                    <div key={r.id} className={'plan-option ' + (form.role === r.id ? 'selected' : '')} onClick={() => setRole(r.id)} style={{ marginBottom: 0 }}>
                      <div className="plan-radio" />
                      <div className="plan-info">
                        <div className="plan-name-row">
                          <span className="plan-name">{r.label}</span>
                          {r.id === 'super_admin' && <span className="badge danger" style={{ fontSize: 10 }}><span className="dot"></span>Acesso total</span>}
                          {r.id === 'admin' && <span className="badge info" style={{ fontSize: 10 }}><span className="dot"></span>Admin</span>}
                          {r.id === 'suporte' && <span className="badge neutral" style={{ fontSize: 10 }}><span className="dot"></span>Suporte</span>}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{r.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="drawer-section">
                <h4>Acesso & status</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {isNew && (
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.sendInvite} onChange={set('sendInvite')} style={{ width: 15, height: 15, marginTop: 2, accentColor: 'var(--brand-navy)', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>Enviar convite por e-mail</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>O usuário receberá um link para definir a senha e acessar o Super Admin.</div>
                      </div>
                    </label>
                  )}
                  {!isNew && (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.active} onChange={set('active')} style={{ width: 15, height: 15, accentColor: 'var(--brand-navy)' }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 500 }}>Conta ativa</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>Desativar revoga o acesso imediatamente.</div>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </>
          )}

          {tab === 'permissions' && (
            <>
              {form.role === 'super_admin' && (
                <div style={{ marginBottom: 16, background: 'var(--danger-bg)', color: 'var(--danger)', borderRadius: 'var(--radius)', padding: '10px 12px', fontSize: 12, display: 'flex', gap: 8 }}>
                  <Icon name="alert" size={14} style={{ flexShrink: 0 }} />
                  <span>Super admins têm acesso total e irrestrito. As permissões individuais não se aplicam.</span>
                </div>
              )}
              {form.role === 'admin' && (
                <div style={{ marginBottom: 16, background: 'var(--info-bg)', color: 'var(--info)', borderRadius: 'var(--radius)', padding: '10px 12px', fontSize: 12, display: 'flex', gap: 8 }}>
                  <Icon name="alert" size={14} style={{ flexShrink: 0 }} />
                  <span>As permissões abaixo foram pré-configuradas para o perfil Admin. Você pode ajustá-las individualmente.</span>
                </div>
              )}

              {permGroups.map(group => (
                <div key={group} className="drawer-section">
                  <h4>{group}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0, borderRadius: 'var(--radius)', overflow: 'hidden', border: '1px solid var(--border)' }}>
                    {PERMS.filter(p => p.group === group).map((p, i, arr) => {
                      const checked = form.role === 'super_admin' ? true : !!form.permissions[p.id];
                      return (
                        <label key={p.id} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '11px 14px',
                          borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none',
                          cursor: form.role === 'super_admin' ? 'default' : 'pointer',
                          background: checked ? 'color-mix(in srgb, var(--brand-navy) 3%, var(--bg-elevated))' : 'var(--bg-elevated)',
                          opacity: form.role === 'super_admin' ? 0.8 : 1,
                          transition: 'background 100ms',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => togglePerm(p.id)}
                            disabled={form.role === 'super_admin'}
                            style={{ width: 15, height: 15, accentColor: 'var(--brand-navy)', flexShrink: 0 }}
                          />
                          <span style={{ fontSize: 13, color: checked ? 'var(--text)' : 'var(--text-secondary)' }}>{p.label}</span>
                          {form.role === 'super_admin' && <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>sempre ativo</span>}
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="drawer-foot">
          <span className="note">
            {isNew ? (form.sendInvite ? 'Convite será enviado por e-mail.' : 'Conta criada sem convite.') : 'Alterações aplicadas imediatamente.'}
          </span>
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={handleSave}>
            {isNew ? <><Icon name="plus" size={14} />Criar usuário</> : <><Icon name="check" size={14} />Salvar</>}
          </button>
        </div>
      </div>
    </>
  );
};

// Default permissions by role
const ROLE_DEFAULTS = {
  super_admin: { viewStores: true, editStores: true, suspendStores: true, impersonate: true, viewFinancial: true, editPlans: true, manageUsers: true, viewAudit: true },
  admin: { viewStores: true, editStores: true, suspendStores: true, impersonate: true, viewFinancial: true, editPlans: true, manageUsers: false, viewAudit: true },
  suporte: { viewStores: true, editStores: false, suspendStores: false, impersonate: true, viewFinancial: false, editPlans: false, manageUsers: false, viewAudit: true },
};

export {
  ROLE_DEFAULTS,
  UserEditorDrawer,
};
